import { flow, types } from 'mobx-state-tree'
import { addSourceAccountConfig } from '../../network/addSourceAccountConfig'
import { getGroupIdsReq } from '../../network/getGroupIdsReq'
import { getSourceAccConfig } from '../../network/getSourceAccConfig'
import { getSubGroupIdsReq } from '../../network/getSubGroupIdsReq'
import { MessageTypes, getErrorMessage } from '../../utils'
import { RootStore } from '../store'

const PaymentMode = types.model('paymentMode', {
  id: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
})

const AccConfig = types.model('AccConfig', {
  id: types.optional(types.number, -1),
  accNumber: types.optional(types.string, ''),
  ifscCode: types.optional(types.string, ''),
  accType: types.optional(types.string, ''),
  accountName: types.optional(types.string, ''),
  clientCode: types.optional(types.string, ''),
  startDate: types.optional(types.number, new Date().getTime() + 3600000),
  endDate: types.optional(types.number, new Date().getTime() + 86400000),
  remarks: types.optional(types.string, ''),
  transactionModes: types.array(PaymentMode),
  methodTypes: types.array(PaymentMode),
  methodSubTypes: types.array(PaymentMode),
  entityType: types.array(PaymentMode),
  processingEntity: types.optional(types.string, ''),
})

const SubGroupId = types.model('subGroupId', {
  id: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
})

const GroupId = types.model('groupId', {
  id: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
  subGroupIds: types.optional(types.array(SubGroupId), []),
})

const SourceAccountConfig = types
  .model('SourceAccountConfig', {
    accConfig: types.array(AccConfig),
    groupIds: types.optional(types.array(GroupId), []),
  })
  .actions((self) => ({
    fetchSourceAccConfig: flow(function* fetchSourceAccConfig(groupId: string, subGroupId: string) {
      try {
        const response = yield getSourceAccConfig(RootStore.session.getTokens, groupId, subGroupId)
        self.accConfig = response
        return response
      } catch (error) {
        self.accConfig = [] as any
        if (getErrorMessage(error) !== 'Payout source account not found') {
          RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        }
        throw error
      }
    }),
    addSourceAccConfig: flow(function* addSourceAccConfig(data: any, isAdd: boolean) {
      try {
        const keysToBeValidated = ['methodTypes', 'methodSubTypes', 'transactionModes', 'entityType']
        keysToBeValidated.forEach((key) => {
          if (data?.[key]?.filter((item) => item?.isChecked)?.length === 0) {
            RootStore.message.setMessage('Please select atleast 1 ' + key, MessageTypes.ERROR)
            throw { code: -1, message: 'Please select atleast 1 ' + key }
          }
        })

        const processingEntities = RootStore.processingEntity.getProcessingEntityListForSourceAccount()
        const processingEntity = processingEntities?.find((item) => item.value === data?.processingEntity)?.id
        const response = yield addSourceAccountConfig(
          RootStore.session.getTokens,
          { ...data, processingEntity: processingEntity },
          isAdd,
        )
        self.accConfig = [] as any
        RootStore.message.setMessage('Success', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchGroupIds: flow(function* fetchGroupIds() {
      try {
        const response = yield getGroupIdsReq(RootStore.session.getTokens)
        self.groupIds = response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchSubGroupIds: flow(function* fetchSubGroupIds(groupId) {
      try {
        const response = yield getSubGroupIdsReq(RootStore.session.getTokens, groupId)
        const retData = self.groupIds?.map((item) => {
          if (item.value === groupId) {
            return {
              ...item,
              subGroupIds: response,
            }
          } else {
            return item
          }
        })
        self.groupIds = JSON.parse(JSON.stringify(retData))
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
  }))
  .views((self) => ({
    getSourceAccountConfig(methodTypes, methodSubTypes, entityTypesVal, tnxModesVal, isAdd) {
      const modes = (config: any) => {
        return methodTypes?.map((item: any) => {
          return {
            id: item?.id,
            mode: item?.value,
            isChecked: !!config?.methodTypes?.find((obj) => obj.id === item?.id) ?? false,
          }
        })
      }

      const subModes = (config: any) => {
        return methodSubTypes?.map((item: any) => {
          return {
            id: item?.id,
            mode: item?.value,
            isChecked: !!config?.methodSubTypes?.find((obj) => obj.id === item?.id) ?? false,
          }
        })
      }
      const entityTypes = (config: any) => {
        return entityTypesVal?.map((item: any) => {
          return {
            id: item?.id,
            mode: item?.value,
            isChecked: !!config?.entityType?.find((obj) => obj.id === item?.id) ?? false,
          }
        })
      }

      const tnxModes = (config: any) => {
        return tnxModesVal?.map((item: any) => {
          return {
            id: item?.id,
            mode: item?.value,
            isChecked: !!config?.transactionModes?.find((obj) => obj.id === item?.id) ?? false,
          }
        })
      }

      const processingEntities = RootStore.processingEntity.getProcessingEntityListForSourceAccount()
      if (isAdd) {
        return [
          ...self?.accConfig,
          {
            methodTypes: modes({}),
            methodSubTypes: subModes({}),
            entityType: entityTypes({}),
            transactionModes: tnxModes({}),
            processingEntity: '',
            isNew: true,
          },
        ]
      }
      const retData = self.accConfig?.map((obj: any) => {
        return {
          ...obj,
          methodTypes: modes(obj),
          methodSubTypes: subModes(obj),
          entityType: entityTypes(obj),
          transactionModes: tnxModes(obj),
          processingEntity: processingEntities?.find((item) => item.id === obj?.processingEntity)?.value ?? '',
          isNew: false,
        }
      })
      return retData
    },
    getGroupids() {
      return self.groupIds?.map((item) => {
        return {
          id: item?.id,
          value: item?.value,
        }
      })
    },
    getSubGroupids(groupId) {
      const obj = self.groupIds?.find((item) => item.value === groupId)
      return obj?.subGroupIds ?? []
    },
  }))

const sourceAccConfigData = {
  accConfig: [],
  groupIds: [],
}

export { SourceAccountConfig, sourceAccConfigData }
